// Context rail — NOTES & QUESTIONS beside the board (Screen 3).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../../design/brainTheme";
import { EmptyState, Icon } from "../../../mui";
import { JIRA_BROWSE } from "../../../mui/actionRegisterStatus";
import {
  listProjectActions,
  updateProjectAction,
  type ProjectAction,
} from "../../../lib/brain-api";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

function isOpen(a: ProjectAction): boolean {
  return a.status !== "done" && a.status !== "dismissed";
}

function isNoteOrQuestion(a: ProjectAction): a is ProjectAction & { kind: "note" | "question" } {
  return a.kind === "note" || a.kind === "question";
}

function formatRailDate(a: ProjectAction): string {
  if (a.createdAt != null) {
    return new Date(a.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }
  if (a.nzDate) {
    const ms = Date.parse(`${a.nzDate}T12:00:00`);
    if (!Number.isNaN(ms)) {
      return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    }
    return a.nzDate;
  }
  return "—";
}

function forWhomLabel(assignee?: string): string | null {
  const name = assignee?.trim();
  if (!name) return null;
  return name.split(/\s+/)[0] ?? name;
}

function NoteCard({
  item,
  resolving,
  onResolve,
}: {
  item: ProjectAction;
  resolving: boolean;
  onResolve: () => void;
}) {
  const open = isOpen(item);
  const isQ = item.kind === "question";
  const forWhom = isQ ? forWhomLabel(item.assignee) : null;
  const issueKey = item.jiraIssueKey?.trim() || null;

  return (
    <Box
      sx={{
        p: "11px 12px",
        border: `1px solid ${isQ ? c.warnBorder : c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        background: isQ ? c.warnBg : c.pageBg,
        opacity: open ? 1 : 0.6,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: isQ ? c.warnText : c.faint,
            whiteSpace: "nowrap",
            flex: "none",
          }}
        >
          {isQ ? "QUESTION" : "NOTE"}
        </Box>
        {forWhom && (
          <Box
            sx={{
              fontSize: 11,
              color: c.warnText,
              background: c.warnBg,
              borderRadius: `${brainTokens.radius.pill}px`,
              px: "7px",
              py: "1px",
              flex: "none",
            }}
          >
            for {forWhom}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <ButtonBase
          onClick={() => {
            if (!open || resolving) return;
            onResolve();
          }}
          disabled={!open || resolving}
          title={open ? "Resolve" : "Resolved"}
          aria-label={open ? "Resolve" : "Resolved"}
          sx={{
            color: open ? c.faint : c.successText,
            display: "inline-flex",
            p: "2px",
            borderRadius: "4px",
            cursor: open ? "pointer" : "default",
            opacity: resolving ? 0.5 : 1,
            "&:hover": open ? { color: c.successText } : undefined,
          }}
        >
          <Icon name="check" size={14} />
        </ButtonBase>
      </Box>

      <Box
        sx={{
          mt: "6px",
          fontSize: 13,
          lineHeight: 1.5,
          color: open ? c.ink2 : c.muted2,
          textWrap: "pretty",
        }}
      >
        {item.text}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          mt: "9px",
          fontSize: 11.5,
          color: c.muted2,
        }}
      >
        <Box component="span">{item.author?.trim() || "—"}</Box>
        <Box component="span" sx={{ color: c.placeholder }}>
          ·
        </Box>
        <Box component="span" sx={{ fontFamily: MONO }}>
          {formatRailDate(item)}
        </Box>
        {issueKey && (
          <>
            <Box sx={{ flex: 1 }} />
            <Box
              component="a"
              href={`${JIRA_BROWSE}/${encodeURIComponent(issueKey)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                color: c.tealInk,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {issueKey}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export function ContextRail({
  projectId,
  refreshKey = 0,
}: {
  projectId: string;
  /** Bump to re-fetch (e.g. after AddWorkBar creates a note). */
  refreshKey?: number;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProjectAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const all = await listProjectActions({ project: projectId });
      const notes = all.filter(isNoteOrQuestion);
      notes.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      setItems(notes);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load notes", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, projectId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  const openItems = useMemo(() => items.filter(isOpen), [items]);
  const resolvedItems = useMemo(() => items.filter((a) => !isOpen(a)), [items]);
  const visible = showResolved ? [...openItems, ...resolvedItems] : openItems;

  const resolve = async (id: string) => {
    setResolvingId(id);
    const prev = items;
    setItems((rows) => rows.map((r) => (r.id === id ? { ...r, status: "done" } : r)));
    try {
      const updated = await updateProjectAction(id, { status: "done" });
      setItems((rows) => rows.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      setItems(prev);
      enqueueSnackbar(e instanceof Error ? e.message : "Could not resolve", {
        variant: "error",
      });
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Box
      sx={{
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        overflow: "hidden",
        minHeight: 220,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "11px 16px",
          background: c.panelBg2,
          borderBottom: `1px solid ${c.borderLight}`,
        }}
      >
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: c.ink,
          }}
        >
          NOTES & QUESTIONS
        </Box>
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 10.5,
            color: c.ink,
            background: c.white,
            border: `1px solid ${c.borderLight}`,
            borderRadius: `${brainTokens.radius.pill}px`,
            px: "6px",
          }}
        >
          {openItems.length}
        </Box>
      </Box>

      <Box sx={{ p: "12px" }}>
        {loading && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={18} sx={{ color: c.teal }} />
          </Box>
        ) : visible.length === 0 && resolvedItems.length === 0 ? (
          <EmptyState icon="clipboard-list" title="Nothing noted yet">
            Note something for the next person, or ask a question.
          </EmptyState>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {visible.map((item) => (
              <NoteCard
                key={item.id}
                item={item}
                resolving={resolvingId === item.id}
                onResolve={() => void resolve(item.id)}
              />
            ))}
          </Box>
        )}

        {resolvedItems.length > 0 && (
          <ButtonBase
            onClick={() => setShowResolved((v) => !v)}
            sx={{
              display: "block",
              mt: "12px",
              fontSize: 12,
              color: c.muted,
              fontFamily: brainTokens.font.sans,
              textAlign: "left",
              "&:hover": { color: c.tealInk },
            }}
          >
            {showResolved ? "Hide resolved" : `Show resolved (${resolvedItems.length})`}
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}
